/** Anhänge (Bild/Video/Audio inline, sonst Datei-Karte), Spoiler-Schleier und Bild-Lightbox. */
import type { AttachmentDto } from '@lagerfeuer/shared';
import { Download, ExternalLink, FileText, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatBytes, formatDuration } from './format';

/** Nur eigene API-Pfade oder http(s) als Ziel (keine javascript:/data:-URLs aus fremden Daten). */
export function safeUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return /^https?:\/\//i.test(url) ? url : null;
}

const kindOf = (a: Pick<AttachmentDto, 'contentType' | 'filename'>): 'image' | 'video' | 'audio' | 'file' => {
  const t = a.contentType ?? '';
  if (/^image\/(png|jpe?g|gif|webp|avif|apng)$/i.test(t)) return 'image';
  if (/^video\/(mp4|webm|ogg|quicktime)$/i.test(t)) return 'video';
  if (/^audio\//i.test(t)) return 'audio';
  return 'file';
};

/** Anzeigemaße innerhalb eines Rahmens (Seitenverhältnis bleibt). */
export function fitSize(w: number | null, h: number | null, maxW: number, maxH: number): { width: number; height: number } | null {
  if (!w || !h) return null;
  const s = Math.min(1, maxW / w, maxH / h);
  return { width: Math.round(w * s), height: Math.round(h * s) };
}

export interface LightboxImage {
  url: string;
  alt: string;
  original: string;
}

export function Lightbox({ image, onClose }: { image: LightboxImage; onClose: () => void }) {
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);
  return createPortal(
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={image.alt || 'Bildansicht'} onClick={onClose} data-testid="lightbox">
      <img src={image.url} alt={image.alt} onClick={(e) => e.stopPropagation()} />
      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <a href={image.original} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-ghost" data-testid="lightbox-open">
          <ExternalLink size={14} /> Original öffnen
        </a>
        <button className="icon-btn" onClick={onClose} aria-label="Schließen" autoFocus data-testid="lightbox-close">
          <X size={20} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

function Spoiler({ children, label }: { children: React.ReactNode; label: string }) {
  const [shown, setShown] = useState(false);
  if (shown) return <>{children}</>;
  return (
    <div className="attachment-spoiler">
      <div className="attachment-spoiler-inner" aria-hidden="true" inert>
        {children}
      </div>
      <button type="button" className="attachment-spoiler-label" onClick={() => setShown(true)} aria-label={`Spoiler anzeigen: ${label}`} data-testid="attachment-spoiler-reveal">
        SPOILER
      </button>
    </div>
  );
}

function AttachmentView({ a, compact, onOpen }: { a: AttachmentDto; compact?: boolean; onOpen: (img: LightboxImage) => void }) {
  const url = safeUrl(a.url);
  const preview = safeUrl(a.previewUrl) ?? url;
  const kind = kindOf(a);
  const maxW = compact ? 280 : 400;
  const maxH = compact ? 220 : 320;
  if (!url) return null;
  let body: React.ReactNode;
  if (kind === 'image' && preview) {
    const size = fitSize(a.width, a.height, maxW, maxH);
    body = (
      <button type="button" className="attachment-image" onClick={() => onOpen({ url, alt: a.description ?? a.filename, original: url })} aria-label={`Bild ${a.filename} vergrößern`} data-testid="attachment-image">
        <img src={preview} alt={a.description ?? a.filename} width={size?.width} height={size?.height} loading="lazy" style={size ? undefined : { maxWidth: maxW, maxHeight: maxH }} />
      </button>
    );
  } else if (kind === 'video') {
    const size = fitSize(a.width, a.height, maxW, maxH);
    body = (
      <video className="attachment-video" src={url} poster={safeUrl(a.previewUrl) ?? undefined} controls preload="metadata" width={size?.width} height={size?.height} data-testid="attachment-video">
        <a href={url}>{a.filename}</a>
      </video>
    );
  } else if (kind === 'audio') {
    body = (
      <div className="attachment-audio" data-testid="attachment-audio">
        <div className="attachment-file-meta">
          <FileText size={20} aria-hidden="true" />
          <a href={url} target="_blank" rel="noopener noreferrer" className="truncate" download={a.filename}>
            {a.filename}
          </a>
          <span className="faint small">
            {formatBytes(a.size)}
            {a.durationSecs ? ` · ${formatDuration(a.durationSecs)}` : ''}
          </span>
        </div>
        <audio src={url} controls preload="none" />
      </div>
    );
  } else {
    body = (
      <div className="attachment-file" data-testid="attachment-file">
        <FileText size={28} aria-hidden="true" />
        <div className="attachment-file-text">
          <a href={url} target="_blank" rel="noopener noreferrer" className="truncate" download={a.filename}>
            {a.filename}
          </a>
          <span className="faint small" data-testid="attachment-size">
            {formatBytes(a.size)}
          </span>
        </div>
        <a className="icon-btn" href={url} download={a.filename} aria-label={`${a.filename} herunterladen`} data-testid="attachment-download">
          <Download size={18} />
        </a>
      </div>
    );
  }
  return (
    <div className="attachment" data-testid="message-attachment" data-kind={kind}>
      {a.spoiler ? <Spoiler label={a.filename}>{body}</Spoiler> : body}
    </div>
  );
}

export function Attachments({ attachments, compact }: { attachments: AttachmentDto[]; compact?: boolean }) {
  const [open, setOpen] = useState<LightboxImage | null>(null);
  if (!attachments.length) return null;
  return (
    <div className="attachments">
      {attachments.map((a) => (
        <AttachmentView key={a.id} a={a} compact={compact} onOpen={setOpen} />
      ))}
      {open && <Lightbox image={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
